import { useState } from 'react'

import { updateChatroomMessage } from '../../../api'
import { EMPTY_LIST } from '../../../constants/common'
import { useNotification, useUserData } from '../../../hooks'
import { isApiNotFoundStatus } from '../../../utils/api'
import { FormikForm } from '../../Formik'
import { CHATROOM_INPUT_FIELD_NAME_MESSAGE } from '../Chatroom.constants'
import {
  getChatroomMessageDeletedUpdate,
  useChatroomMessageDelete,
} from '../ChatroomMessage'

import {
  getChatroomMessageEditInitialValues,
  getChatroomMessageEditPayload,
  isChatroomMessageEditEmpty,
} from './ChatroomMessageEditForm.utils'
import { ChatroomMessageEditFormFields } from './ChatroomMessageEditFormFields'

function ChatroomMessageEditForm({
  children,
  isFullScreen,
  message,
  messageDraftRef,
  outboundUuid,
  onFinishEdit,
  onSaved,
  onUpdateMessage,
}) {
  const currentUser = useUserData()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [files, setFiles] = useState(EMPTY_LIST)

  // The files already on the message are only detached on remove, never
  // deleted — same handling as documents attached from the storage. The mount
  // snapshot is what "changed" is measured against, not the live `message`.
  const [initialFiles] = useState(message.files || EMPTY_LIST)
  const [storageFiles, setStorageFiles] = useState(initialFiles)

  const [modal, setModal] = useState(null)

  const [isSubmitting, setSubmitting] = useState(false)

  function handleMessageNotFound() {
    handleAddErrorNotification('Chatroom.MessageEditDeletedErrorKey')
  }

  const { deleteConfirmationModal, handleClickDelete } =
    useChatroomMessageDelete({
      message,
      outboundUuid,
      onDeleted: onFinishEdit,
      onNotFound: handleMessageNotFound,
      onUpdateMessage,
    })

  function handleCloseModal() {
    setModal(null)
  }

  async function handleUpdateMessage(text) {
    setSubmitting(true)

    return updateChatroomMessage({
      data: getChatroomMessageEditPayload({
        files: [...storageFiles, ...files],
        text,
      }),
      outboundUuid,
      uuid: message.uuid,
    })
      .then(response => {
        if (response) {
          onSaved(response.data)
          handleAddSuccessNotification('Chatroom.MessageEditSuccessKey')
          onFinishEdit()
        }
      })
      .catch(error => {
        if (isApiNotFoundStatus(error?.response?.status)) {
          handleMessageNotFound()
          onUpdateMessage(
            message.uuid,
            getChatroomMessageDeletedUpdate({
              currentUserId: currentUser?.id,
              message,
            })
          )
          onFinishEdit()
        } else {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  // Awaits the request so Formik clears its own `isSubmitting` once it settles.
  async function handleSubmit(values) {
    const text = values[CHATROOM_INPUT_FIELD_NAME_MESSAGE]

    if (
      isChatroomMessageEditEmpty({ files: [...storageFiles, ...files], text })
    ) {
      handleClickDelete()
    } else {
      await handleUpdateMessage(text)
    }
  }

  return (
    <FormikForm
      initialValues={getChatroomMessageEditInitialValues(message)}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <ChatroomMessageEditFormFields
        files={files}
        initialFiles={initialFiles}
        isFullScreen={isFullScreen}
        isSubmitting={isSubmitting}
        message={message}
        messageDraftRef={messageDraftRef}
        outboundUuid={outboundUuid}
        storageFiles={storageFiles}
        onCancel={onFinishEdit}
        onCloseModal={handleCloseModal}
        onSetFiles={setFiles}
        onSetModal={setModal}
        onSetStorageFiles={setStorageFiles}
      >
        {children}
      </ChatroomMessageEditFormFields>
      {modal || deleteConfirmationModal}
    </FormikForm>
  )
}

export default ChatroomMessageEditForm
